import { DefaultThunkAction } from "./shared";
import actions from "../constants/actions.json";
import { AnyAction } from "redux";
import {
  setAdSettingsWorkState,
  setTransactionsFetchWorkState,
  setProjectsFetchWorkState,
} from "./current";
import Log from "../utils/Log";
import { formatUNIXTimestamp } from "../utils/helperUtils";
import { WorkState, AdPaymentDescription } from "../reducers/models";
import {
  fetchAdPaymentHistory,
  fetchHyperWalletUser,
  fetchProjects,
  fetchRegistrationStatus,
  createHyperWalletUser,
  updateHyperWalletUser,
  fetchAdboxReport,
  fetchAdboxMonthToDate,
  fetchAdboxDailyMetrics
} from "../api/monetization";
import { dispatchError } from "./error";
import { validateToken } from "./auth";
import { HWAccountStatus, HWVerificationStatus } from "../reducers/models";
import { toast } from "react-toastify";

//fetches exports/games on /account/ads/games

export function fetchGames(): DefaultThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const { auth } = getState();
    const { accessToken } = auth?.authTokens;
    dispatch(setProjectsFetchWorkState(WorkState.Loading));

    try {
      const projectExportsObject = await fetchProjects(accessToken);
      // @ts-ignore
      const projects = projectExportsObject.payload.map(
        (project: bb.model.monetization.IAdProject) => {
          const {
            bundleId,
            lifetimeRevenueCents,
            targetPlatform,
            lastUpdate,
            fullGameName,
            app,
          } = project;
          return {
            app: app,
            projectName: fullGameName,
            bundleId: bundleId,
            platform: targetPlatform,
            lifetimeRevenueCents: formatCentsToDollars(lifetimeRevenueCents),
            lastUpdate: formatUNIXTimestamp(lastUpdate),
            subRows: [],
          };
        }
      );

      dispatch(setProjects(projects));
      projects.length > 0
        ? dispatch(setProjectsFetchWorkState(WorkState.Success))
        : dispatch(setProjectsFetchWorkState(WorkState.None));
    } catch (e:any) {
      Log.error(e, "error caught in fetchGames");
      dispatch(dispatchError(e));
      dispatch(setProjectsFetchWorkState(WorkState.Error));
    }
  };
}

// fetches ad revenue payments for /account/ads/earnings page
export function fetchAdPaymentTransactions(): DefaultThunkAction<
  Promise<void>
> {
  return async (dispatch) => {
    dispatch(setTransactionsFetchWorkState(WorkState.Loading));
    try {
      const refreshedToken = await dispatch<Promise<string>>(validateToken());

      const paymentHistoryObject = await fetchAdPaymentHistory(refreshedToken);
      let transactionsArray: bb.model.monetization.IAdPaymentFormatted[] = [];

      if (paymentHistoryObject) {
        // @ts-ignore
        await paymentHistoryObject.payload.forEach(
          (job: bb.model.monetization.IAdPaymentPayload) => {
            return job.transactions.forEach(
              (transaction: bb.model.monetization.IAdPaymentTransaction) => {
                const {
                  paymentLines,
                  id,
                  paidAt,
                  userShareTotal,
                  revSharePercentage,
                  description,
                } = transaction;
                const formatted = formatPaymentLines(paymentLines, id);
                const bundleIdQuantity = paymentLines.length;

                const transactionLine = {
                  id: id,
                  paidAt: formatUNIXTimestamp(paidAt),
                  userShareTotal: formatCentsToDollars(userShareTotal),
                  revSharePercentage: `${revSharePercentage}%`,
                  description:
                    description === AdPaymentDescription.AUTOMATED
                      ? "-"
                      : "Adjusted",
                  subRows: bundleIdQuantity > 1 ? formatted : [],
                  bundleId:
                    bundleIdQuantity > 1
                      ? `${bundleIdQuantity} IDS`
                      : paymentLines.length
                      ? paymentLines[0].bundleId
                      : "-",
                };
                transactionsArray.push(transactionLine);
              }
            );
          }
        );
        dispatch(setTransactions(transactionsArray));
      }

      transactionsArray.length > 0
        ? dispatch(setTransactionsFetchWorkState(WorkState.Success))
        : dispatch(setTransactionsFetchWorkState(WorkState.None));

      dispatch(setTransactionsFetchWorkState(WorkState.None));
    } catch (e:any) {
      Log.error(e, "error caught in fetchAdPaymentTransactions");
      dispatch(dispatchError(e));
      dispatch(setTransactionsFetchWorkState(WorkState.Error));
    }
  };
}

export const formatCentsToDollars = (cents: number) => {
  const dollars = (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
  return `US ${dollars}`;
};

//used to format for table on /account/ads/earnings
export const formatPaymentLines = (
  paymentLines: bb.model.monetization.IAdPaymentLine[],
  transactionId: string
): bb.model.monetization.IAdPaymentLineFormatted[] => {
  return paymentLines.map((paymentLine) => {
    return {
      id: "-",
      bundleId: paymentLine.bundleId,
      userShareTotal: formatCentsToDollars(paymentLine.userShareTotal),
      platform: paymentLine.platform,
    };
  });
};

//actions below are used in /account/ads/settings

export function fetchHyperwalletStatus(): DefaultThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(setAdSettingsWorkState(WorkState.Loading));
    try {
      const refreshedToken = await dispatch<Promise<string>>(validateToken());
      const response = await fetchRegistrationStatus(refreshedToken);
      const { status, verificationStatus } = response;
      console.log(status, verificationStatus);
      //TODO: set account status from the response later (using this for toggling for now)
      const unregistered: bb.model.monetization.IHWAccountStatus = {
        status: HWAccountStatus.UNREGISTERED,
        verificationStatus: HWVerificationStatus.NOT_REQUIRED,
      };

      if (status === "UNREGISTERED") {
        dispatch(getHyperWalletUser(true));
        dispatch(setHWAccountStatus(unregistered));
      } else {
        dispatch(getHyperWalletUser(false));
      }
      dispatch(setHWAccountStatus({ status, verificationStatus }));
      dispatch(setAdSettingsWorkState(WorkState.None));
    } catch (e:any) {
      dispatch(dispatchError(e));
      Log.error(e, "Error caught in fetchHyperwalletStatus:");
      dispatch(setAdSettingsWorkState(WorkState.Error));
    }
  };
}

export function registerHyperWalletUser(
  userInfo: bb.model.monetization.IHWAccountInfo
): DefaultThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(setAdSettingsWorkState(WorkState.Loading));

    try {
      const accessToken = await dispatch<Promise<string>>(validateToken());
      await createHyperWalletUser(userInfo, accessToken);
      const registered: bb.model.monetization.IHWAccountStatus = {
        status: HWAccountStatus.PRE_ACTIVATED,
        verificationStatus: HWVerificationStatus.VERIFIED,
      };
      toast.success("HW account created successfully!");
      dispatch(setHWAccountInfo(userInfo));
      dispatch(setHWAccountStatus(registered));
      dispatch(setAdSettingsWorkState(WorkState.Success));
    } catch (e:any) {
      Log.error(e, "Error caught in registerHyperWalletUser:");
      dispatch(dispatchError(e));
      dispatch(setAdSettingsWorkState(WorkState.None));
    }
  };
}

export function updateHyperWalletUserInfo(
  userInfo: bb.model.monetization.IHWAccountInfo
): DefaultThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(setAdSettingsWorkState(WorkState.Loading));
    try {
      const accessToken = await dispatch<Promise<string>>(validateToken());
      const response = await updateHyperWalletUser(userInfo, accessToken);
      console.log("response =====", response);
      //TODO: grab real updated user object from response and update store
      // dispatch(setHWAccountInfo(userInfo));
      dispatch(setAdSettingsWorkState(WorkState.Success));
    } catch (e:any) {
      Log.error(e, "Error caught in updateHyperWalletUserInfo:");
      dispatch(dispatchError(e));
      dispatch(setAdSettingsWorkState(WorkState.Error));
    }
  };
}

export function getHyperWalletUser(
  isNewUser: boolean
): DefaultThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(setAdSettingsWorkState(WorkState.Loading));

    try {
      if (isNewUser) {
        const emptyInfo = {
          firstName: "",
          lastName: "",
          dateOfBirth: "",
          email: "",
          country: "",
        };
        dispatch(setHWAccountInfo(emptyInfo));
      } else {
        const accessToken = await dispatch<Promise<string>>(validateToken());
        const response = await fetchHyperWalletUser(accessToken);

        const {
          firstName,
          lastName,
          dateOfBirth,
          email,
          country,
        } = response;

        const userInfo = {
          firstName,
          lastName,
          dateOfBirth,
          email,
          country,
        };
        dispatch(setHWAccountInfo(userInfo));
      }
      dispatch(setAdSettingsWorkState(WorkState.Success));
    } catch (e:any) {
      Log.error(e, "Error caught in getHyperWalletUser:");
      dispatch(dispatchError(e));
      dispatch(setAdSettingsWorkState(WorkState.Error));
    }
  };
}


export function getAdboxReport(startDate:Date, endDate:Date, excludeColumns: Array<string>):DefaultThunkAction<Promise<void>> {
  return async (dispatch) => {
    dispatch(setAdboxReport({
      workState: WorkState.Loading,
      networkReports: undefined
    }))
    try {
      const accessToken = await dispatch<Promise<string>>(validateToken());
      const reportItems = await fetchAdboxReport(accessToken, startDate, endDate, excludeColumns)
    
      dispatch(setAdboxReport({
        workState: WorkState.Success,
        networkReports: reportItems
      }))
    } catch (e:any) {
      Log.error(e, "Error caught generating adbox report")
      dispatch(dispatchError(e))
      dispatch(setAdboxReport({
        workState: WorkState.Error,
        networkReports: undefined
      }))
    }
  }
}

export function getAdboxMonthToDate():DefaultThunkAction<Promise<void>> {
  return async (dispatch) => {
    dispatch(setMonthToDateEarnings({
      workState: WorkState.Loading,
      earnings: 0
    }))
    try{
      const accessToken = await dispatch<Promise<string>>(validateToken());
      const reportEarnings = await fetchAdboxMonthToDate(accessToken)
      dispatch(setMonthToDateEarnings({
        workState: WorkState.Success,
        earnings: reportEarnings.earnings.net
      }))
    } catch (err:any) {
      Log.error(err, "Error caught generating adbox month to date")
      dispatch(dispatchError(err))
      dispatch(setMonthToDateEarnings({
        workState: WorkState.Error,
        earnings: 0
      }))
    }
  }
}

export function getAdboxDailyMetrics(startDate:Date, endDate:Date):DefaultThunkAction<Promise<void>> {
  return async (dispatch) => {
    dispatch(setAdboxDailyMetrics({
      workState: WorkState.Loading,
      dailyMetrics: undefined
    }))
    try{
      const accessToken = await dispatch<Promise<string>>(validateToken());
      const dailyMetrics = await fetchAdboxDailyMetrics(accessToken, startDate, endDate)
      dispatch(setAdboxDailyMetrics({
        workState: WorkState.Success,
        dailyMetrics
      }))
    } catch (err:any) {
      Log.error(err, "Error caught generating adbox month to date")
      dispatch(dispatchError(err))
      dispatch(setAdboxDailyMetrics({
        workState: WorkState.Error,
        dailyMetrics: undefined
      }))
    }
  }
}

export function setHWAccountStatus(
  accountState: bb.model.monetization.IHWAccountStatus
): AnyAction {
  return {
    type: actions.monetization.SET_HW_ACCOUNT_STATE,
    accountState,
  };
}

export function setHWAccountInfo(
  accountInfo: bb.model.monetization.IHWAccountInfo
): AnyAction {
  return {
    type: actions.monetization.SET_HW_ACCOUNT_INFO,
    accountInfo,
  };
}

export function setTransactions(
  transactions: bb.model.monetization.IAdPaymentFormatted[]
): AnyAction {
  return {
    type: actions.monetization.SET_PAYMENT_TRANSACTIONS,
    transactions,
  };
}

export function setProjects(
  projects: bb.model.monetization.IAdProject[]
): AnyAction {
  return {
    type: actions.monetization.SET_PROJECTS,
    projects,
  };
}


// lets see
export function setAdboxReport(
  adboxReport: bb.model.monetization.IAdboxReport
): AnyAction {
  return {
    type: actions.monetization.SET_ADBOX_REPORT,
    adboxReport
  }
}

export function setMonthToDateEarnings(
  monthToDateEarnings: bb.model.monetization.IAdboxMonthToDateEarnings
): AnyAction {
  return {
    type: actions.monetization.SET_MONTH_TO_DATE_EARNINGS,
    monthToDateEarnings
  }
}

export function setbundleFilterWord(
  bundleFilterWord:string
  ):AnyAction{
  return {
    type: actions.monetization.SET_BUNDLE_FILTER_WORD,
    bundleFilterWord
  }
}

export function setPlatformOSFilter(
  filters: bb.model.monetization.osPlatformFilter
  ):AnyAction {
  return {
    type: actions.monetization.SET_PLATFORM_OS_FILTER,
    filters
  }
}

export function setNetworkFilter (
  filters: bb.model.monetization.INetworkFilter
): AnyAction {
  return {
    type: actions.monetization.SET_NETWORK_FILTER,
    filters
  }
}

export function setFilteredTable():AnyAction {
  return {
    type: actions.monetization.SET_FILTERED_TABLE
  }
}

export function setAdboxDailyMetrics(
  adboxDailyMetrics: bb.model.monetization.IAdboxDailyMetrics
): AnyAction {
  return {
    type: actions.monetization.SET_DAILY_METRICS,
    adboxDailyMetrics
  }
}
